"use client"

import * as React from "react"
import { zodResolver } from "@hookform/resolvers/zod"
import { useForm } from "react-hook-form"

import { Button } from "@/components/ui/button"
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form"
import { Input } from "@/components/ui/input"
import * as m from "@/paraglide/messages"
import { createCompany } from "@/lib/api/client/companies"
import { ApiError } from "@/lib/api/client"
import {
  companyStepSchema,
  sanitizeTaxpayerId,
  type CompanyStepValues,
} from "@/lib/signup/schemas"
import { cn } from "@/lib/utils"

import { FormError } from "./form-error"

interface CompanyStepProps {
  onBack?: () => void
  onSuccess: () => void
}

export function CompanyStep({ onBack, onSuccess }: CompanyStepProps) {
  const [serverErrors, setServerErrors] = React.useState<string[]>([])

  const form = useForm<CompanyStepValues>({
    resolver: zodResolver(companyStepSchema),
    defaultValues: { name: "", taxpayerId: "" },
  })

  const onSubmit = form.handleSubmit(async (values) => {
    setServerErrors([])
    try {
      await createCompany(values)
      onSuccess()
    } catch (error) {
      if (error instanceof ApiError) {
        setServerErrors(error.errors)
      } else {
        setServerErrors([m.signup_error_company_generic()])
      }
    }
  })

  return (
    <Form {...form}>
      <form onSubmit={onSubmit} className="grid gap-4" noValidate>
        <FormError errors={serverErrors} />

        <FormField
          control={form.control}
          name="name"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{m.signup_company_name_label()}</FormLabel>
              <FormControl>
                <Input
                  placeholder={m.signup_company_name_placeholder()}
                  autoComplete="organization"
                  {...field}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="taxpayerId"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{m.signup_company_taxpayer_id_label()}</FormLabel>
              <FormControl>
                <Input
                  placeholder={m.signup_company_taxpayer_id_placeholder()}
                  inputMode="numeric"
                  {...field}
                  onChange={(event) =>
                    field.onChange(sanitizeTaxpayerId(event.target.value))
                  }
                />
              </FormControl>
              <FormDescription>
                {m.signup_company_taxpayer_id_description()}
              </FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />

        <div className="mt-2 flex flex-col-reverse gap-2 sm:flex-row sm:justify-between">
          {onBack ? (
            <Button
              type="button"
              variant="outline"
              size="lg"
              onClick={onBack}
              disabled={form.formState.isSubmitting}
            >
              {m.signup_button_back()}
            </Button>
          ) : null}
          <Button
            type="submit"
            size="lg"
            className={cn(!onBack && "sm:ml-auto")}
            disabled={form.formState.isSubmitting}
          >
            {form.formState.isSubmitting
              ? m.signup_button_creating_company()
              : m.signup_button_submit()}
          </Button>
        </div>
      </form>
    </Form>
  )
}
